import type { AssistantTranscriptTimelineItem, OptimisticTranscriptExchange } from "../types";
import { shouldRetainOptimisticActivity } from "./use-optimistic-transcript-exchange";

function hasCanonicalPrompt(
  items: AssistantTranscriptTimelineItem[],
  exchange: OptimisticTranscriptExchange
) {
  return items.some(
    (item) =>
      item.kind === "message" &&
      item.role === "user" &&
      ((exchange.promptMessageId &&
        (item._id === exchange.promptMessageId || item.id === exchange.promptMessageId)) ||
        item.sourceOrder === exchange.sourceOrder)
  );
}

function hasCanonicalQueuedMessage(
  items: AssistantTranscriptTimelineItem[],
  exchange: OptimisticTranscriptExchange
) {
  // Keyed on the queue row, never on the host prompt: every message queued behind the same run
  // shares that prompt id, so matching on it would let the first message's bubble suppress the
  // acknowledgment for the second one the user just sent.
  if (!exchange.queuedInputId) {
    return false;
  }
  return items.some(
    (item) =>
      item.kind === "message" &&
      item.deliveryState === "queued" &&
      item.queuedInputId === exchange.queuedInputId
  );
}

/**
 * Once the run drains a queued row the backend stops projecting its marker — the message itself
 * becomes a real user message in the transcript instead. Without recognising that, the optimistic
 * chip reappears and keeps claiming the message is still waiting for turns after Claudia read it.
 *
 * The drained message carries the queue row it came from, so this is an identity check. Text is not
 * an identity: the same words appear in a repeat prompt earlier in the thread and in an earlier
 * steering message of this very run, and both would retire an acknowledgment that is still waiting.
 */
function hasDrainedSteeringMessage(
  items: AssistantTranscriptTimelineItem[],
  exchange: OptimisticTranscriptExchange
) {
  if (!exchange.queuedInputId) {
    return false;
  }
  return items.some(
    (item) =>
      item.kind === "message" &&
      item.role === "user" &&
      item.queuedInputId === exchange.queuedInputId
  );
}

export function buildOptimisticTranscriptItems(args: {
  exchange: OptimisticTranscriptExchange | null | undefined;
  threadId: string | null;
  items: AssistantTranscriptTimelineItem[];
  promptExecutionPhase: string | null | undefined;
}) {
  const { exchange } = args;
  if (!exchange || exchange.threadId !== args.threadId) {
    return [] as AssistantTranscriptTimelineItem[];
  }

  const promptMessageId = exchange.promptMessageId ?? exchange.temporaryMessageId;

  // RC-8: a steering submission joins the run already in flight, so it gets no exchange of its own.
  // Show it as the user's own queued bubble until the backend projection of the queue row arrives.
  if (exchange.pendingMode === "assistant_steering") {
    if (
      hasCanonicalQueuedMessage(args.items, exchange) ||
      hasDrainedSteeringMessage(args.items, exchange)
    ) {
      return [] as AssistantTranscriptTimelineItem[];
    }
    return [
      {
        kind: "message",
        _creationTime: exchange.createdAt,
        _id: exchange.temporaryMessageId,
        id: exchange.temporaryMessageId,
        key: `optimistic:${exchange.localExchangeId}:queued-input`,
        transcriptItemKey: `optimistic:${exchange.localExchangeId}:queued-input`,
        finality: "streaming",
        // Every projected row names its own thread (LAT-950); an optimistic row is no exception —
        // the transcript drops rows that do not say which conversation they belong to.
        threadId: exchange.threadId,
        sourceOrder: exchange.sourceOrder,
        promptMessageId,
        queuedInputId: exchange.queuedInputId ?? undefined,
        deliveryState: "queued",
        order: exchange.order,
        // Past the host run's activity turn (+1) and its persisted queued bubbles (+0.75).
        stepOrder: 2,
        role: "user",
        status: "pending",
        text: exchange.prompt,
        parts: [
          ...(exchange.prompt.trim().length > 0
            ? [{ type: "text" as const, text: exchange.prompt }]
            : []),
          ...(exchange.attachments ?? []),
        ],
      },
    ] satisfies AssistantTranscriptTimelineItem[];
  }

  const runId = exchange.runId ?? `optimistic:${exchange.localExchangeId}`;
  const hasCanonicalAssistantReply = args.items.some(
    (item) =>
      item.kind === "message" &&
      item.role === "assistant" &&
      item.promptMessageId === promptMessageId
  );
  const hasPersistedActivitySection = args.items.some(
    (item) =>
      item.kind === "activity_turn" &&
      (item.promptMessageId === promptMessageId ||
        item.promptMessageId === exchange.temporaryMessageId ||
        (exchange.runId ? item.runId === exchange.runId : false))
  );
  const optimisticItems: AssistantTranscriptTimelineItem[] = [];

  if (!hasCanonicalPrompt(args.items, exchange)) {
    const parts: Extract<AssistantTranscriptTimelineItem, { kind: "message" }>["parts"] = [
      ...(exchange.prompt.trim().length > 0
        ? [{ type: "text" as const, text: exchange.prompt }]
        : []),
      ...(exchange.attachments ?? []),
    ];
    optimisticItems.push({
      kind: "message",
      _creationTime: exchange.createdAt,
      _id: exchange.temporaryMessageId,
      id: exchange.temporaryMessageId,
      key: `optimistic:${exchange.localExchangeId}:prompt`,
      transcriptItemKey: `optimistic:${exchange.localExchangeId}:prompt`,
      finality: "streaming",
      threadId: exchange.threadId,
      sourceOrder: exchange.sourceOrder,
      order: exchange.order,
      stepOrder: 0,
      role: "user",
      status: "pending",
      text: exchange.prompt,
      parts,
    });
  }

  if (
    shouldRetainOptimisticActivity({
      exchange,
      threadId: args.threadId,
      promptExecutionPhase: args.promptExecutionPhase,
      hasCanonicalAssistantReply,
      hasPersistedActivitySection,
    })
  ) {
    const waitingForUser = args.promptExecutionPhase === "AWAITING_USER_INPUT";
    // A run parked on a `wait` shows what it is doing before the canonical timeline lands: a
    // spinner that says "Thinking" over a run that is deliberately idle reads as a hang.
    const waitingOnTimer = args.promptExecutionPhase === "WAITING";
    optimisticItems.push({
      kind: "activity_turn",
      key: `optimistic:${exchange.localExchangeId}:activity`,
      transcriptItemKey: `optimistic:${exchange.localExchangeId}:activity`,
      finality: "streaming",
      turnId: `optimistic:${exchange.localExchangeId}`,
      segmentIndex: 0,
      segmentKind: "prompt",
      threadId: exchange.threadId,
      runId,
      promptMessageId,
      finalMessageId: null,
      status: "in_progress",
      itemsView: "full",
      items: waitingOnTimer
        ? [
            {
              id: `turn:${runId}:wait:optimistic`,
              kind: "waiting",
              source: "tool",
              text: "Waiting for work in progress",
              status: "waiting",
              orderKey: "wait:optimistic",
              waiting: {
                assistantToolCallId: "optimistic",
                seconds: null,
                reason: "work in progress",
                outcome: null,
                waitedSeconds: null,
                startedAt: exchange.createdAt,
                endedAt: null,
              },
            },
          ]
        : waitingForUser
          ? [
              {
                id: `turn:${runId}:ask-user:optimistic`,
                kind: "user_input",
                source: "tool",
                text: "Waiting for your answer",
                status: "waiting",
                orderKey: "ask-user:optimistic",
                userInput: {
                  requestId: "optimistic",
                  toolCallId: "optimistic",
                  assistantToolCallId: null,
                  questionCount: null,
                  status: "pending",
                  resolvedAt: null,
                  questions: [],
                  answers: {},
                },
              },
            ]
          : [],
      summaryText: waitingOnTimer
        ? "Waiting for work in progress"
        : waitingForUser
          ? "Waiting for your answer"
          : "Thinking...",
      startedAt: exchange.createdAt,
      completedAt: null,
      wallDurationMs: null,
      activeDurationMs: null,
      pausedDurationMs: 0,
      pauseIntervals: waitingOnTimer
        ? [{ kind: "wait", startedAt: exchange.createdAt, endedAt: null, sourceId: "optimistic" }]
        : waitingForUser
          ? [
              {
                kind: "ask_user",
                startedAt: exchange.createdAt,
                endedAt: null,
                sourceId: "optimistic",
              },
            ]
          : [],
      durationMs: null,
      order: exchange.order,
      stepOrder: 1,
    });
  }

  return optimisticItems;
}
